import { QuoteSession } from '../../quote/quote.session';
import { Injectable } from '@angular/core';
import { NumberTypeRequirementsMap } from '../../quote/number-addition/number-addition.types';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class NumberValidationService {
  private numberTypeRequirementsSubject = new BehaviorSubject<NumberTypeRequirementsMap>(null);
  public numberTypeRequirements$: Observable<NumberTypeRequirementsMap> =
    this.numberTypeRequirementsSubject.asObservable();

  constructor(
    private _quoteSession: QuoteSession,
    private _messageService: MessageService,
  ) {
    this.loadNumberTypeRequirements();
  }

  public loadNumberTypeRequirements(): void {
    this._quoteSession.numberTypeRequirementsLoaded.subscribe(
      () => {
        const requirements = this._quoteSession.numberTypeRequirements;
        this.numberTypeRequirementsSubject.next(requirements);
      },
      (error) => {
        console.error('Error loading number type requirements:', error);
      },
    );
  }

  public verifyRequestedFields(
    numberType: string,
    MPC: string,
    description: string,
    erNumber: string,
    fileNumber: string,
    quantity?: string,
  ): boolean {
    let isValidRequest: boolean = true;
    var errorMsgList = [];

    // Verify MPC
    if (!this.isValidMPC(numberType, MPC)) {
      isValidRequest = false;
      errorMsgList.push({
        severity: 'error',
        summary: 'Failed',
        detail: 'Please select a valid MPC from the dropdown or create a new MPC',
      });
    }

    // Verify Description
    if (this.getNumberTypeRequirements()[numberType].requiresDescription && !description) {
      isValidRequest = false;
      errorMsgList.push({ severity: 'error', summary: 'Failed', detail: 'Please specify a description.' });
    }

    // Verify ER Number
    if (!this.isValidER(numberType, erNumber)) {
      isValidRequest = false;
      errorMsgList.push({
        severity: 'error',
        summary: 'Failed',
        detail: 'Please specify one of the ER numbers or create a new ER number.',
      });
    }

    // Verify File Number
    if (!this.isValidFileNumber(numberType, fileNumber)) {
      isValidRequest = false;
      errorMsgList.push({
        severity: 'error',
        summary: 'Failed',
        detail: 'Please specify an existing file number or create a new File Number.',
      });
    }

    // Verify Quantity
    if (Number(quantity) <= 0 || Number(quantity) == undefined || Number(quantity) > 20) {
      isValidRequest = false;
      errorMsgList.push({
        severity: 'error',
        summary: 'Failed',
        detail: 'Quantity of Numbers must be between 1 and 20.',
      });
    }

    this._messageService.addAll(errorMsgList);
    return isValidRequest;
  }

  private isValidMPC(numberType: string, majorProductCode: string): boolean {
    const requirements = this.getNumberTypeRequirements()[numberType].requiresMPC;
    if (requirements && !majorProductCode) {
      return false;
    }

    if (majorProductCode) {
      let majorProductCodes: any = this._quoteSession.mpcs;
      for (var i = 0; i < majorProductCodes.length; i++) {
        if (majorProductCode == majorProductCodes[i]['mpc']) {
          return true;
        }
      }
      return false;
    }

    return true;
  }

  private isValidER(numberType: string, erNumber: string) {
    const requirements = this.getNumberTypeRequirements()[numberType].requiresERNumber;
    if (requirements && !erNumber) {
      return false;
    }

    if (erNumber) {
      let erNumbers: any = this._quoteSession.erNumbers;
      for (var i = 0; i < erNumbers.length; i++) {
        if (erNumber.toUpperCase() == erNumbers[i]['formattedNumber']) {
          return true;
        }
      }
      return false;
    }

    return true;
  }

  private isValidFileNumber(numberType: string, fileNumber: string): boolean {
    if (numberType === 'Manufacturing Engineering Project') {
      return !!fileNumber && this._quoteSession.fileNumbers.includes(fileNumber);
    }

    return !fileNumber;
  }

  public getNumberTypeRequirements(): NumberTypeRequirementsMap {
    return this.numberTypeRequirementsSubject.value;
  }
}
